/**
 * 认证工具模块
 * 处理Token存储、获取、验证等功能
 */

const TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_INFO_KEY = 'user_info'

/**
 * 获取访问Token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * 设置访问Token
 */
export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * 获取刷新Token
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * 设置刷新Token
 */
export function setRefreshToken(token) {
  localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

/**
 * 获取用户信息
 */
export function getUserInfo() {
  const info = localStorage.getItem(USER_INFO_KEY)
  if (!info || info === 'undefined' || info === 'null') {
    return null
  }
  try {
    return JSON.parse(info)
  } catch (e) {
    console.error('解析用户信息失败:', e)
    return null
  }
}

/**
 * 设置用户信息
 */
export function setUserInfo(userInfo) {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo))
}

/**
 * 清除所有认证信息
 */
export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_INFO_KEY)
}

/**
 * 检查是否已登录
 */
export function isLoggedIn() {
  return !!getToken()
}

/**
 * 获取用户权限列表
 */
export function getPermissions() {
  const userInfo = getUserInfo()
  return userInfo?.permissions || []
}

/**
 * 获取用户菜单权限
 */
export function getMenus() {
  const userInfo = getUserInfo()
  return userInfo?.menus || []
}

/**
 * 获取用户按钮权限
 */
export function getButtons() {
  const userInfo = getUserInfo()
  return userInfo?.buttons || []
}

/**
 * 检查是否有指定权限
 */
export function hasPermission(permission) {
  const userInfo = getUserInfo()
  if (!userInfo) return false
  
  // 超级管理员拥有所有权限
  if (userInfo.is_superuser) return true
  
  const permissions = userInfo.permissions || []
  return permissions.includes(permission)
}

/**
 * 检查是否有任意一个权限
 */
export function hasAnyPermission(permissions) {
  if (!Array.isArray(permissions)) return false
  return permissions.some(perm => hasPermission(perm))
}

/**
 * 检查是否有所有权限
 */
export function hasAllPermissions(permissions) {
  if (!Array.isArray(permissions)) return false
  return permissions.every(perm => hasPermission(perm))
}

/**
 * 检查是否有指定菜单权限
 */
export function hasMenu(menuCode) {
  const userInfo = getUserInfo()
  if (!userInfo) return false
  
  if (userInfo.is_superuser) return true
  
  const menus = userInfo.menus || []
  // menus 是对象数组，检查 code 或 permission_code 字段
  return menus.some(menu => menu.code === menuCode || menu.permission_code === menuCode)
}

/**
 * 检查是否有指定按钮权限
 */
export function hasButton(buttonCode) {
  const userInfo = getUserInfo()
  if (!userInfo) return false
  
  if (userInfo.is_superuser) return true
  
  const buttons = userInfo.buttons || []
  return buttons.includes(buttonCode)
}

/**
 * 获取用户角色
 */
export function getRoles() {
  const userInfo = getUserInfo()
  return userInfo?.roles || []
}

/**
 * 检查是否是超级管理员
 */
export function isSuperuser() {
  const userInfo = getUserInfo()
  return userInfo?.is_superuser || false
}

/**
 * 解析JWT Token（不验证签名）
 */
export function parseToken(token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (e) {
    return null
  }
}

/**
 * 检查Token是否即将过期（默认5分钟内）
 */
export function isTokenExpiringSoon(token, thresholdMinutes = 5) {
  const payload = parseToken(token)
  if (!payload || !payload.exp) return true
  
  const expTime = payload.exp * 1000
  const now = Date.now()
  const threshold = thresholdMinutes * 60 * 1000
  
  return expTime - now < threshold
}

/**
 * 获取Token过期时间
 */
export function getTokenExpireTime(token) {
  const payload = parseToken(token)
  return payload?.exp ? payload.exp * 1000 : null
}
